import api from './client';
import { getTenantSubdomain } from '../config/api';

export interface AuthPublicConfig {
  demoSelfRegister: boolean;
  allowPublicRegister: boolean;
  googleClientId: string | null;
  googleAuthEnabled: boolean;
  instanceEnvironment: string;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  lastLoginAt?: string | null;
  authProvider?: string;
}

export interface AuthSession {
  message: string;
  token: string;
  user: AuthUser;
}

function isTransientAuthError(err: unknown): boolean {
  const e = err as { status?: number; message?: string; name?: string };
  if (e?.status && e.status < 500) return false;
  const msg = `${e?.name || ''} ${e?.message || ''}`;
  return !e?.status || e.status >= 500 || /timeout|abort|fetch|network|connection/i.test(msg);
}

/** Autostop tenants often miss the first request; retry instead of a dead network error. */
async function withTenantWakeRetry<T>(fn: () => Promise<T>): Promise<T> {
  if (!getTenantSubdomain()) return fn();
  let last: unknown;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    try {
      return await fn();
    } catch (err) {
      last = err;
      if (!isTransientAuthError(err) || attempt === 3) throw err;
      await new Promise((r) => setTimeout(r, 2500 * (attempt + 1)));
    }
  }
  throw last;
}

export const authApi = {
  getConfig: async (): Promise<AuthPublicConfig> => {
    return withTenantWakeRetry(async () => {
      const { data } = await api.get<AuthPublicConfig>('auth/config');
      return data;
    });
  },
  login: async (email: string, password: string): Promise<AuthSession> => {
    return withTenantWakeRetry(async () => {
      const { data } = await api.post<AuthSession>('auth/login', { email, password });
      return data;
    });
  },
  register: async (payload: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<AuthSession> => {
    const { data } = await api.post<AuthSession>('auth/register', payload);
    return data;
  },
  google: async (idToken: string): Promise<AuthSession> => {
    return withTenantWakeRetry(async () => {
      const { data } = await api.post<AuthSession>('auth/google', { idToken });
      return data;
    });
  },
  setPassword: async (token: string, password: string): Promise<AuthSession> => {
    return withTenantWakeRetry(async () => {
      const { data } = await api.post<AuthSession>('auth/set-password', { token, password });
      return data;
    });
  },
};
